import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { Contract } from '../../types';
import { useCurrencies } from '../../hooks/useCurrencies';
import { resolveCurrencyDocumentId } from '../../utils/currencyUtils';

/**
 * Currency handling for the contract form: API-backed options, saved-value
 * normalization on edit, and rate suggestion for non-EGP currencies.
 */
export function useContractCurrency(
  contract: Contract | undefined,
  control: any,
  setValue: (name: any, value: any) => void
) {
  const { data: currencies = [] } = useCurrencies();

  // Saved currency may be a populated object or a documentId string
  const savedCurrencyId = contract ? resolveCurrencyDocumentId(contract.currency, currencies) : '';

  // Re-apply the saved currency once options are loaded (edit case)
  useEffect(() => {
    if (savedCurrencyId && currencies.length > 0) {
      setValue('currency', savedCurrencyId);
    }
  }, [savedCurrencyId, currencies, setValue]);

  const currencyOptions = useMemo(() => (
    (currencies as any[])
      .filter((c: any) => c.isActive !== false)
      .map((c: any) => ({
        value: c.documentId || String(c.id),
        label: `${c.code || ''} (${c.name || c.code || 'Unknown'})`
      }))
  ), [currencies]);

  const selectedCurrencyId = useWatch({ control, name: 'currency' });
  const selectedCurrency = useMemo(
    () => (currencies as any[]).find(
      (c: any) => c.documentId === selectedCurrencyId || String(c.id) === String(selectedCurrencyId)
    ),
    [currencies, selectedCurrencyId]
  );
  const isForeign = !!selectedCurrency && selectedCurrency.code !== 'EGP';

  // Suggest the currency's stored rate when switching to a foreign currency
  useEffect(() => {
    if (isForeign && selectedCurrency?.exchangeRateToEGP && !contract) {
      setValue('exchangeRate', selectedCurrency.exchangeRateToEGP);
    }
  }, [isForeign, selectedCurrency, contract, setValue]);

  return { currencyOptions, selectedCurrency, isForeign };
}
